import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signIn, completeLogin } from '../lib/auth';
import { isDeviceTrusted, sendDeviceVerification, verifyDevice } from '../lib/deviceAuth';

interface AuthFormProps {
  mode: 'login' | 'register';
}

interface InvitationData {
  email: string;
  role: string;
  study_association_id: string;
  study_association_name: string;
  member_id: string | null;
  member_name: string | null;
}

export default function AuthForm({ mode }: AuthFormProps) {
  const [searchParams] = useSearchParams();
  const urlInvitationToken = searchParams.get('invitation');

  // Token kan komme fra URL ELLER fra manuell input (fallback hvis e-postklient
  // trunkerte lenken). Manual-input state vises kun ved /register uten URL-token,
  // eller ved invitationError fra URL-token.
  const [manualToken, setManualToken] = useState('');
  const invitationToken = urlInvitationToken || (manualToken || null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  // studyAssociationId/memberId holdes kun fra invitasjonen (ikke eksponert i UI).
  const [, setStudyAssociationId] = useState('');
  const [, setMemberId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [needsDeviceVerification, setNeedsDeviceVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);

  // SECURITY: Store pending session until device verification completes
  const [pendingSession, setPendingSession] = useState<{ token: string; user: any } | null>(null);

  // Invitation state
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(false);
  const [invitationError, setInvitationError] = useState('');

  // Verify invitation token if present
  useEffect(() => {
    if (mode === 'register' && invitationToken) {
      setLoadingInvitation(true);
      setInvitationError('');

      fetch(`/api/auth/verify-invitation/${invitationToken}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.invitation) {
            setInvitation(data.invitation);
            setEmail(data.invitation.email);
            setStudyAssociationId(data.invitation.study_association_id);
            if (data.invitation.member_id) {
              setMemberId(data.invitation.member_id);
            }
          } else {
            setInvitationError(data.error || 'Ugyldig eller utløpt invitasjon');
          }
        })
        .catch((err) => {
          console.error('Failed to verify invitation:', err);
          setInvitationError('Kunne ikke verifisere invitasjon');
        })
        .finally(() => {
          setLoadingInvitation(false);
        });
    }
  }, [mode, invitationToken]);

  // Selvsignup-flyten er fjernet — vi henter ikke lenger lister over
  // studieforbund/medlemsorganisasjoner. All kontekst kommer fra invitasjonen.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        // Sign in but DON'T save session yet (security fix)
        const result = await signIn({ email, password }, false);

        // Check if device is trusted (if user wants device to be trusted)
        if (trustDevice) {
          // Pass user ID from login result since session is not saved yet
          const trusted = await isDeviceTrusted(result.user.id);
          if (!trusted) {
            // SECURITY: Store session in memory, not localStorage
            // Will only be saved after device verification completes
            setPendingSession({
              token: result.session.access_token,
              user: result.user
            });
            // Send verification code to user's email
            await sendDeviceVerification('Current Device', result.user.id);
            setNeedsDeviceVerification(true);
            setLoading(false);
            return;
          }
        }

        // Device is trusted or user chose not to trust device
        // NOW save the session and proceed to dashboard
        completeLogin(result.session.access_token, result.user);
        window.location.href = '/';
      } else {
        // Handle invitation-based registration
        if (invitation && invitationToken) {
          // Register via invitation
          const response = await fetch('/api/auth/register-invited', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: invitationToken,
              password,
              full_name: fullName,
            }),
          });

          const result = await response.json();

          if (!result.success) {
            setError(result.error || 'Registrering feilet');
            setLoading(false);
            return;
          }

          // Show success message - invited users can log in immediately
          setRegistrationSuccess(true);
        } else {
          // Uten gyldig invitasjon er registrering ikke mulig — backend
          // returnerer uansett 410 på /auth/register. Dette er en defensiv
          // sjekk fordi skjemaet normalt ikke renderes uten invitasjon.
          setError('Registrering krever gyldig invitasjon fra medlem-administrator.');
          setLoading(false);
          return;
        }
      }
    } catch (err: any) {
      setError(err.message || 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Pass user ID from pendingSession since session is not saved yet
      const userId = pendingSession?.user?.id;
      const verified = await verifyDevice(verificationCode, 'Current Device', userId);
      if (verified) {
        // SECURITY: Only NOW save the session after device verification
        if (pendingSession) {
          completeLogin(pendingSession.token, pendingSession.user);
          setPendingSession(null);
        }
        window.location.href = '/';
      } else {
        setError('Ugyldig eller utløpt verifiseringskode');
      }
    } catch (err: any) {
      setError(err.message || 'Verifisering feilet');
    } finally {
      setLoading(false);
    }
  };

  // Show success message after registration
  if (mode === 'register' && registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Registrering vellykket!
            </h2>
          </div>
          {invitation ? (
            // Invited user - can log in immediately
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">
                Velkommen! Din brukerkonto er nå opprettet.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Du kan nå logge inn med e-postadressen <strong>{email}</strong> og passordet du valgte.
              </p>
            </div>
          ) : (
            // Regular user - needs email verification
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">
                En e-post med verifiseringslenke har blitt sendt til <strong>{email}</strong>.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Vennligst sjekk innboksen din og klikk på lenken for å verifisere kontoen din.
              </p>
              <p className="text-sm text-green-800 mt-2">
                Du kan ikke logge inn før e-postadressen er verifisert.
              </p>
            </div>
          )}
          <div className="text-center space-y-2">
            <a
              href="/login"
              className="text-sm text-kf-primary hover:text-kf-secondary"
            >
              {invitation ? 'Gå til innlogging' : 'Tilbake til innlogging'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show device verification screen
  if (needsDeviceVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verifiser enhet
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Vi har sendt en verifiseringskode til <strong>{email}</strong>
            </p>
            <p className="mt-1 text-center text-xs text-gray-500">
              Koden utløper om 15 minutter
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleVerifyDevice}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <div>
              <label htmlFor="code" className="label">
                Verifiseringskode
              </label>
              <input
                id="code"
                type="text"
                required
                className="input text-center text-2xl tracking-widest"
                placeholder="XXXXXX"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                maxLength={6}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500">
                Finner du ikke e-posten? Sjekk søppelpost-mappen.
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              {loading ? 'Verifiserer...' : 'Verifiser'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {mode === 'login' ? 'Logg inn' : 'Registrer deg'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Kompetanseattest Kvalitetssikring
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* SECURITY (2026-10-30): Selvbetjent registrering er deaktivert i backend
              (POST /auth/register returnerer 410). Siden vises nå kun for
              invitasjons-flyten. Hvis bruker lander her uten gyldig invitation,
              informerer vi om at nye brukere må inviteres av medlem-admin. */}
          {mode === 'register' && !invitation && !loadingInvitation && !urlInvitationToken && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">
                Nye brukere registreres kun gjennom invitasjon
              </p>
              <p className="text-xs text-blue-800">
                Medlem-administrator i din organisasjon sender invitasjon på e-post.
                Hvis du har mottatt en invitasjonskode, kan du lime den inn i boksen nedenfor.
              </p>
              <a href="/login" className="inline-block mt-3 text-sm text-blue-700 underline">
                ← Tilbake til innlogging
              </a>
            </div>
          )}

          {/* Manuell token-input — vises ved /register uten URL-token,
              eller ved invitationError (e-postklient kan ha trunkert lenken). */}
          {mode === 'register' && (!urlInvitationToken || invitationError) && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-900 font-medium mb-2">
                Har du fått en invitasjonskode på e-post?
              </p>
              <p className="text-xs text-amber-800 mb-3">
                Hvis lenken i e-posten ikke virker (noen e-postklienter klipper lange lenker),
                kan du kopiere selve invitasjonskoden og lime den inn her.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Lim inn invitasjonskoden"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value.trim())}
                  className="flex-1 rounded-md border border-amber-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (manualToken) {
                      // Trigger ny verifisering via state-oppdatering
                      setInvitationError('');
                    }
                  }}
                  disabled={!manualToken || manualToken.length < 8}
                  className="rounded-md bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  Bruk kode
                </button>
              </div>
            </div>
          )}

          {/* Invitation banner */}
          {mode === 'register' && invitationToken && (
            loadingInvitation ? (
              <div className="rounded-md bg-kf-blue-50 p-4">
                <p className="text-sm text-kf-blue-800">Verifiserer invitasjon...</p>
              </div>
            ) : invitationError ? (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800 font-medium">Invitasjonsfeil</p>
                <p className="text-sm text-red-700 mt-1">{invitationError}</p>
                <p className="text-sm text-red-700 mt-2">
                  Kontakt din medlem-administrator for ny invitasjon.
                </p>
              </div>
            ) : invitation && (
              <div className="rounded-md bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-800 font-medium">Du har blitt invitert!</p>
                <div className="mt-2 text-sm text-green-700 space-y-1">
                  <p><strong>Studieforbund:</strong> {invitation.study_association_name}</p>
                  {invitation.member_name && (
                    <p><strong>Organisasjon:</strong> {invitation.member_name}</p>
                  )}
                  <p><strong>Rolle:</strong> {invitation.role === 'admin' ? 'Administrator' : invitation.role === 'sensor' ? 'Sensor' : 'Medlem'}</p>
                </div>
              </div>
            )
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Signup-feltene og submit vises kun for login, eller når invitasjon er lastet.
              Uten gyldig invitasjon er registreringsflyten blokkert av backend (410). */}
          {(mode === 'login' || invitation) && (
            <>
              <div className="space-y-4">
                {mode === 'register' && invitation && (
                  <div>
                    <label htmlFor="fullName" className="label">
                      Fullt navn
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      required
                      className="input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={loadingInvitation || !!invitationError}
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="label">
                    E-postadresse
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className={`input ${invitation ? 'bg-gray-100' : ''}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={!!invitation}
                    disabled={loadingInvitation || !!invitationError}
                  />
                  {invitation && (
                    <p className="mt-1 text-xs text-gray-500">E-postadressen er satt fra invitasjonen</p>
                  )}
                </div>
                <div>
                  <label htmlFor="password" className="label">
                    Passord
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loadingInvitation || !!invitationError}
                  />
                </div>
              </div>

              {mode === 'login' && (
                <div className="flex items-center">
                  <input
                    id="trustDevice"
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="h-4 w-4 text-kf-primary focus:ring-kf-primary border-gray-300 rounded"
                  />
                  <label htmlFor="trustDevice" className="ml-2 block text-sm text-gray-700">
                    Husk meg på denne enheten (30 dager)
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || loadingInvitation || !!invitationError}
                className="btn btn-primary w-full"
              >
                {loading ? 'Vent...' : mode === 'login' ? 'Logg inn' : 'Opprett konto'}
              </button>
            </>
          )}

          <div className="space-y-2">
            {mode === 'login' && (
              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-gray-600 hover:text-kf-primary"
                >
                  Glemt passord?
                </a>
              </div>
            )}
            {/* Lenke fra register tilbake til login (invitasjons-flyt) — selvsignup-vei fra login er fjernet. */}
            {mode === 'register' && (
              <div className="text-center">
                <a
                  href="/login"
                  className="text-sm text-kf-primary hover:text-kf-secondary"
                >
                  Har du allerede konto? Logg inn
                </a>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
